// src/components/Shu.jsx
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

export default function Shu({ api }) {
  const [data, setData] = useState([]);
  const [params, setParams] = useState(null);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Parameter input (default mengikuti contoh SHU 2025)
  const [form, setForm] = useState({
    year: 2025,
    margin_pensiun: 4460000,
    beban: 39835721.45,
    modal_pct: 35,
    tabungan_pct: 40,
    pinjaman_pct: 60,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleChange = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/shu", { params: form });
      setData(res.data.data || []);
      setParams(res.data.params || null);
      setTotals(res.data.totals || null);
      setCurrentPage(1);
    } catch (e) {
      console.error("Error fetching SHU:", e);
      setError("Gagal memuat data SHU. Pastikan backend berjalan & token valid.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.ceil(data.length / itemsPerPage);
  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // ─── Export Excel ──────────────────────────────────────────────────────────
  const exportExcel = () => {
    if (!data.length) return;

    const rows = data.map((t, i) => ({
      No: i + 1,
      "No Anggota": t.member_no,
      Nama: t.name,
      "Tab Pokok": t.pokok,
      "Tab Wajib": t.wajib,
      "Tab Sukarela": t.sukarela,
      "Tab Lain": t.lain,
      "Total Tabungan (E)": t.total_tabungan,
      "Margin Pinjaman (F)": t.margin,
      "SHU Jasa Tabungan": Math.round(t.shu_tabungan),
      "SHU Jasa Pinjaman": Math.round(t.shu_pinjaman),
      "Total SHU": t.total_shu,
    }));

    rows.push({
      No: "",
      "No Anggota": "",
      Nama: "TOTAL",
      "Tab Pokok": "",
      "Tab Wajib": "",
      "Tab Sukarela": "",
      "Tab Lain": "",
      "Total Tabungan (E)": params?.total_e || "",
      "Margin Pinjaman (F)": params?.total_f || "",
      "SHU Jasa Tabungan": Math.round(totals?.shu_tabungan || 0),
      "SHU Jasa Pinjaman": Math.round(totals?.shu_pinjaman || 0),
      "Total SHU": totals?.total_shu || 0,
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    // Sheet kedua: parameter
    const paramRows = [
      ["Parameter SHU", form.year],
      ["Total Margin Pinjaman (DB)", params?.total_margin_pinjaman],
      ["Margin Anggota Pensiun", params?.margin_pensiun],
      ["Total Pendapatan", params?.total_pendapatan],
      ["Beban (Pajak/admin/dll)", params?.beban],
      [`Penambahan Modal (${params?.modal_pct}%)`, params?.penambahan_modal],
      ["Total Pengeluaran", params?.total_pengeluaran],
      ["Total SHU", params?.total_shu],
      [`SHU Tabungan (${params?.tabungan_pct}%)`, params?.shu_tabungan],
      [`SHU Pinjaman (${params?.pinjaman_pct}%)`, params?.shu_pinjaman],
    ];
    const wsParam = XLSX.utils.aoa_to_sheet(paramRows);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `SHU ${form.year}`);
    XLSX.utils.book_append_sheet(wb, wsParam, "Parameter");
    XLSX.writeFile(wb, `SHU_Koperasi_${form.year}.xlsx`);
  };

  // ─── Export PDF ────────────────────────────────────────────────────────────
  const exportPDF = () => {
    if (!data.length) return;

    const doc = new jsPDF({ orientation: "landscape" });

    doc.setFontSize(14);
    doc.text(`Daftar SHU Anggota Koperasi Tahun ${form.year}`, 14, 15);
    doc.setFontSize(9);
    doc.text(
      `Total SHU: ${params?.total_shu_formatted || "-"}  |  ` +
        `SHU Tabungan: ${params?.shu_tabungan_formatted || "-"}  |  ` +
        `SHU Pinjaman: ${params?.shu_pinjaman_formatted || "-"}`,
      14,
      22
    );

    const tableData = data.map((t, i) => [
      i + 1,
      t.member_no,
      t.name,
      rupiah(t.total_tabungan),
      rupiah(t.margin),
      rupiah(Math.round(t.shu_tabungan)),
      rupiah(Math.round(t.shu_pinjaman)),
      rupiah(t.total_shu),
    ]);

    autoTable(doc, {
      head: [["No", "No Anggota", "Nama", "Total Tabungan", "Margin", "SHU Tabungan", "SHU Pinjaman", "Total SHU"]],
      body: tableData,
      startY: 28,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [59, 130, 246] },
      foot: [[
        { content: "TOTAL", colSpan: 7, styles: { halign: "right", fontStyle: "bold" } },
        { content: totals?.total_shu_formatted || "-", styles: { fontStyle: "bold" } },
      ]],
    });

    doc.save(`SHU_Koperasi_${form.year}.pdf`);
  };

  const fields = [
    { key: "year", label: "Tahun Buku", step: 1 },
    { key: "margin_pensiun", label: "Margin Anggota Pensiun (Rp)" },
    { key: "beban", label: "Beban Pajak/Admin/dll (Rp)" },
    { key: "modal_pct", label: "Penambahan Modal (%)" },
    { key: "tabungan_pct", label: "Porsi SHU Tabungan (%)" },
    { key: "pinjaman_pct", label: "Porsi SHU Pinjaman (%)" },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold text-slate-800">SHU (Sisa Hasil Usaha)</h1>

      {/* Panel Parameter */}
      <div className="rounded-2xl border bg-white p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Parameter Perhitungan</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-xs text-slate-500">{f.label}</label>
              <input
                type="number"
                step={f.step || "any"}
                value={form[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className="w-full border rounded-xl px-3 py-2 text-sm mt-1"
              />
            </div>
          ))}
        </div>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-700"
        >
          Hitung SHU
        </button>
      </div>

      {/* Ringkasan parameter hasil */}
      {params && (
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl shadow-sm p-5 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <p className="text-sm opacity-90">Total SHU</p>
            <p className="text-2xl font-bold mt-1">{params.total_shu_formatted}</p>
            <p className="text-xs opacity-80 mt-1">Pendapatan {params.total_pendapatan_formatted}</p>
          </div>
          <div className="rounded-2xl shadow-sm p-5 bg-gradient-to-br from-green-500 to-green-600 text-white">
            <p className="text-sm opacity-90">SHU Jasa Tabungan ({params.tabungan_pct}%)</p>
            <p className="text-2xl font-bold mt-1">{params.shu_tabungan_formatted}</p>
            <p className="text-xs opacity-80 mt-1">{data.length} anggota</p>
          </div>
          <div className="rounded-2xl shadow-sm p-5 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <p className="text-sm opacity-90">SHU Jasa Pinjaman ({params.pinjaman_pct}%)</p>
            <p className="text-2xl font-bold mt-1">{params.shu_pinjaman_formatted}</p>
            <p className="text-xs opacity-80 mt-1">Margin {rupiah(params.total_f)}</p>
          </div>
        </div>
      )}

      {/* Tombol Export */}
      <div className="flex gap-3">
        <button
          onClick={exportExcel}
          disabled={!data.length}
          className="px-3 py-2 rounded-xl bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-50"
        >
          Export Excel
        </button>
        <button
          onClick={exportPDF}
          disabled={!data.length}
          className="px-3 py-2 rounded-xl bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
        >
          Export PDF
        </button>
      </div>

      {error && (
        <div className="rounded-2xl bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Tabel */}
      <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-4 py-3">No</th>
              <th className="text-left px-4 py-3">No Anggota</th>
              <th className="text-left px-4 py-3">Nama</th>
              <th className="text-right px-4 py-3">Total Tabungan</th>
              <th className="text-right px-4 py-3">Margin</th>
              <th className="text-right px-4 py-3">SHU Tabungan</th>
              <th className="text-right px-4 py-3">SHU Pinjaman</th>
              <th className="text-right px-4 py-3">Total SHU</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-6 text-slate-400">Memuat data...</td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-6 text-slate-400">Tidak ada data</td>
              </tr>
            ) : (
              paginatedData.map((t, i) => (
                <tr key={i} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500">
                    {(currentPage - 1) * itemsPerPage + i + 1}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{t.member_no}</td>
                  <td className="px-4 py-3 font-medium">{t.name}</td>
                  <td className="px-4 py-3 text-right text-slate-600">{t.total_tabungan_formatted}</td>
                  <td className="px-4 py-3 text-right text-slate-600">{t.margin_formatted}</td>
                  <td className="px-4 py-3 text-right text-green-700">{t.shu_tabungan_formatted}</td>
                  <td className="px-4 py-3 text-right text-orange-700">{t.shu_pinjaman_formatted}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800">{t.total_shu_formatted}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {!loading && data.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 text-sm border-t bg-slate-50">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-slate-600">
              Page <b>{currentPage}</b> dari <b>{totalPages}</b>
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
